import React, { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { useNavigate } from '@lib/react-router-dom'

import { useResolvedAppTestProduct } from '@pages/ProductRegistration/AppropriatenessTest/hooks/useResolvedAppTestProduct'

import { useAccountReadContext, useAccountWriteContext } from '@context/AccountContextContext'
import { useLegalLinksContext } from '@context/LegalLinksContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'

import { Button } from '@ui/components/Button'
import { ContentBlock } from '@ui/components/ContentBlock/ContentBlock'
import { FeatureIcon } from '@ui/components/FeatureIcon/FeatureIcon'
import { Link } from '@ui/components/Link'
import { WarningIcon } from '@ui/icons/Warning.icon'

import {
  isEconomicProfileTestNeeded,
  showApptestRiskScreen,
} from '@utils/AccountAccess/accountProductStatuses'
import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { getProductEconomicProfilePath } from '@utils/path'

import { TestAnswersDto } from '@model/ClientTestAnswerDto'

import { useLandingPage } from '@hooks/useLandingPage'
import { useLinksConfig } from '@hooks/useLinksConfig'

import { Box } from '@shared/components/Box'
import { FullScreenFooter } from '@shared/templates/FullScreenLayout'
import { TestStatusTemplateInnerWrapper } from '@shared/templates/TestStatusTemplate/TestStatusTemplate'

export const ProductAppTestRiskAcceptancePage: React.FC = () => {
  const { account } = useAccountReadContext()

  const navigate = useNavigate()
  const apiClient = useApiClient(ClientApiClient)
  const resolvedProductId = useResolvedAppTestProduct()
  const locale = useSessionLanguage()
  const { refreshAccount } = useAccountWriteContext()
  const [answers, setAnswers] = useState<TestAnswersDto | undefined>()
  const [loading, setLoading] = useState<boolean>(false)
  const { t } = useTranslation()
  const { links } = useLinksConfig()
  const { docLinks } = useLegalLinksContext()
  const { getLandingPage } = useLandingPage()
  const economicProfilePath = getProductEconomicProfilePath(resolvedProductId)

  useEffect(() => {
    if (!account) return
    if (!showApptestRiskScreen(account, resolvedProductId)) {
      navigate(
        isEconomicProfileTestNeeded(account, resolvedProductId)
          ? economicProfilePath
          : getLandingPage(resolvedProductId),
        { replace: true }
      )
    }
  }, [account, economicProfilePath, getLandingPage, navigate, resolvedProductId])

  useEffect(() => {
    if (account && !showApptestRiskScreen(account, resolvedProductId)) return

    let cancelled = false
    const load = async () => {
      try {
        setLoading(true)
        const a = await apiClient.getAccountAppTestAnswers(locale, resolvedProductId)
        if (!cancelled) setAnswers(a)
      } finally {
        setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [account, apiClient, locale, resolvedProductId])

  const handleAccept = async () => {
    if (!account?.id || !answers?.id) return
    await apiClient.acceptAppTestRisk({
      clientId: account.id,
      clientTestId: answers.id,
      riskAccepted: true,
    })
    await refreshAccount(locale)
    navigate(economicProfilePath)
  }

  const handleDecline = async () => {
    if (account?.id && answers?.id) {
      await apiClient.acceptAppTestRisk({
        clientId: account.id,
        clientTestId: answers.id,
        riskAccepted: false,
      })
    }
    navigate('/logout')
  }

  return (
    <TestStatusTemplateInnerWrapper>
      <Box flex fullWidth direction='column' gap='x32' align='center'>
        <ContentBlock
          variant='voucher'
          icon={<FeatureIcon size='lg' icon={WarningIcon} bg='surface-warning-high-em' />}
          title={t('Sign up.Proceed with caution')}
          description={[
            <Trans
              key='marginally-pass-message'
              i18nKey='Sign up.Marginally pass message'
              components={{
                a: (
                  <Link
                    href={docLinks?.riskDisclosure ?? links.footer.TICKMILL_EU.riskDisclosure}
                    target='_blank'
                    size='lg'
                    rel='noopener noreferrer'
                  />
                ),
              }}
            />,
          ]}
        />
        <FullScreenFooter>
          <Button.Primary fullWidth size='lg' onClick={handleAccept} disabled={loading}>
            {t('Yes I accept the Risk')}
          </Button.Primary>
          <Button.Secondary fullWidth size='lg' onClick={handleDecline}>
            {t('No I do not accept the Risk')}
          </Button.Secondary>
        </FullScreenFooter>
      </Box>
    </TestStatusTemplateInnerWrapper>
  )
}
